/**
 * MCP UI Control Hook
 *
 * Listens for UI control commands from the MCP server (via main process)
 * and executes them by calling workspace store actions.
 *
 * Also mirrors lock state to AI Workspace .meta files for file-based agents.
 */

import { useEffect, useCallback } from 'react';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useAiLockStore, AiLockInfo } from '../../terminal/store/aiLockStore';
import { useAiExecutionStore, AI_EXECUTION_SQL_CHANGED } from '../../terminal/store/aiExecutionStore';
import { useTerminalStore } from '../../terminal/store/terminalStore';
import type {
  NewQueryTabInput,
  NewTableExplorerTabInput,
  NewNotebookTabInput,
  TabConnectionInfo,
  TableExplorerTabContent,
  FilterGroup,
  SortDefinition,
} from '../../workspace/core/types/workspaceTypes';
import {
  createDefaultTableExplorerContent,
  isTableExplorerTab,
  isStorageExplorerTab,
  isNotebookTab,
} from '../../workspace/core/types/workspaceTypes';
import { entityTabAsTableExplorerView, setEntityTabFilter } from '../handlers/entityTabAsTableExplorerView';
import type { NotebookTabContent, NotebookCell } from '../../workspace/notebook/types/notebookTypes';
import { isCodeCell, isExecutableCell, createCodeCell, createMarkdownCell } from '../../workspace/notebook/types/notebookTypes';
import type {
  UiControlCommandPayload,
  UiOpenTabInput,
  UiOpenTabResult,
  UiFocusTabResult,
  UiSetEditorTextInput,
  UiSetEditorTextResult,
  UiCloseTabInput,
  UiCloseTabResult,
  UiLockTabInput,
  UiLockTabResult,
  UiUnlockTabInput,
  UiUnlockTabResult,
  UiCheckAbortInput,
  UiCheckAbortResult,
  UiConnectToDatabaseInput,
  UiConnectToDatabaseResult,
  UiExecuteQueryInput,
  UiExecuteQueryResult,
  UiGetExecutionUnitsInput,
  UiGetExecutionUnitsResult,
  UiControlResult,
  UiSetTableFilterInput,
  UiSetTableFilterResult,
  UiSetTableSortInput,
  UiSetTableSortResult,
  UiSetTablePaginationInput,
  UiSetTablePaginationResult,
  UiGetTableSnapshotInput,
  UiGetTableSnapshotResult,
  TableExplorerSnapshot,
  McpWriteApprovalRequest,
  McpConnectionsAddApprovalRequest,
  McpMetaInfoDeclareApprovalRequest,
  UiListTabsResult,
  UiRefreshTableInput,
  UiRefreshTableResult,
  UiShowChartInput,
  UiShowChartResult,
  UiSetChartConfigInput,
  UiSetChartConfigResult,
  UiSelectRowInput,
  UiSelectRowResult,
  UiShowRowDetailsInput,
  UiShowRowDetailsResult,
  McpChartConfig,
  // Query editor types
  UiQuerySelectRowInput,
  UiQuerySelectRowResult,
  UiQueryShowRowDetailsInput,
  UiQueryShowRowDetailsResult,
  UiQuerySwitchTabInput,
  UiQuerySwitchTabResult,
  UiQuerySetResultDisplayInput,
  UiQuerySetResultDisplayResult,
  UiQueryExecuteWithPlanInput,
  UiQueryExecuteWithPlanResult,
  UiQueryGetExecutionPlanInput,
  UiQueryGetExecutionPlanResult,
  UiQueryGetMessagesInput,
  UiQueryGetMessagesResult,
  UiQuerySetChartConfigInput,
  UiQuerySetChartConfigResult,
  UiQueryShowChartInput,
  UiQueryShowChartResult,
  UiQueryGetResultsInput,
  UiQueryGetResultsResult,
  UiGetPlsqlEditorContentInput,
  UiGetPlsqlEditorContentResult,
  UiPackageSelectInput,
  UiPackageSelectResult,
  UiPackageNavigateMemberInput,
  UiPackageNavigateMemberResult,
  UiExportResultsInput,
  UiExportResultsResult,
} from '../../../types/mcpUiControl';
import { getGridExportRunner } from '../../workspace/common/store/gridExportRegistry';
import { handleStorageExplorerOpenTab, refuseTabTypeForConnection } from '../handlers/storageOpenTabHandler';
import { useTableExplorerMcpStore } from '../../workspace/table-explorer/store/tableExplorerMcpStore';
import { useQueryEditorMcpStore } from '../../workspace/query-editor/store/queryEditorMcpStore';
import type { ChartConfig, AggregatedColumnMapping } from '../../workspace/charts/types/chartTypes';
import { resolveChartColumnRef, type ChartRefColumn } from '../../workspace/charts/utils/chartColumnRefs';
import { useWriteApprovalStore } from '../store/writeApprovalStore';
import { usePairingApprovalStore } from '../store/pairingApprovalStore';
import { useConnectionsAddApprovalStore } from '../store/connectionsAddApprovalStore';
import { useMetaInfoDeclareApprovalStore } from '../store/metaInfoDeclareApprovalStore';
import type { McpPairingApprovalRequest } from '../../../types/mcpPairing';
import { isQueryTab, isPlSqlEditorTab, type QueryTabContent } from '../../workspace/core/types/workspaceTypes';
import { findExistingPackageExplorerTab, createDefaultPackageExplorerContent } from '../../workspace/package-explorer/types';
import { usePackagePinsStore } from '../../workspace/package-explorer/store/packagePinsStore';
import {
  handleNotebookListCells,
  handleNotebookGetCell,
  handleNotebookAddCell,
  handleNotebookSetCellSource,
  handleNotebookDeleteCell,
  handleNotebookMoveCell,
  handleNotebookConvertCell,
  handleNotebookClearOutputs,
} from '../handlers/notebookMcpHandlers';
import type {
  UiNotebookRunCellInput,
  UiNotebookRunCellResult,
  UiNotebookRunAllInput,
  UiNotebookRunAllResult,
  UiNotebookStopExecutionResult,
  UiNotebookRestartSessionInput,
  UiNotebookRestartSessionResult,
  UiNotebookSaveInput,
  UiNotebookSaveResult,
  UiNotebookSetChartConfigInput,
  UiNotebookSetChartConfigResult,
} from '../../../types/mcpUiControl';
import { redactSecretText, safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';
import { hasUnsavedStoragePreview } from '../../workspace/storage-explorer/preview/storagePreviewSessionStore';

/** MCP session ID for tracking lock ownership */
const MCP_SESSION_ID = 'mcp-server';

/**
 * Mirror lock state to all active AI Workspace sessions
 * This ensures file-based agents can see MCP-managed locks via .meta files
 */
function blueprintLockToAiWorkspaces(tabId: string, message?: string): void {
  const sessions = useTerminalStore.getState().sessions;
  const aiSessions = sessions.filter((s) => s.type === 'ai');

  for (const session of aiSessions) {
    window.electronAPI?.aiWorkspace?.writeTabLock(session.id, tabId, message).catch((err) => {
      console.error(`[MCP UI Control] Failed to mirror lock to session ${session.id}:`, err);
    });
  }
}

/**
 * Mirror unlock state to all active AI Workspace sessions
 * This ensures file-based agents can see MCP-managed unlocks via .meta file removal
 */
function blueprintUnlockToAiWorkspaces(tabId: string): void {
  const sessions = useTerminalStore.getState().sessions;
  const aiSessions = sessions.filter((s) => s.type === 'ai');

  for (const session of aiSessions) {
    window.electronAPI?.aiWorkspace?.deleteTabLock(session.id, tabId).catch((err) => {
      console.error(`[MCP UI Control] Failed to mirror unlock to session ${session.id}:`, err);
    });
  }
}

/**
 * Handle open_tab command
 */
const handleOpenTab = (input: UiOpenTabInput): UiOpenTabResult => {
  try {
    // ---- azure-storage (Task 1.18): capability gate + the storage browser branch.
    const refused = refuseTabTypeForConnection(input);
    if (refused) return refused;
    if (input.type === 'storage-explorer') return handleStorageExplorerOpenTab(input);

    const { addTab, setActiveTab } = useWorkspaceStore.getState();

    if (input.type === 'query') {
      // Build connection info if provided, resolving name/host from the connections store
      let connectionInfo: TabConnectionInfo | undefined;
      if (input.connectionId) {
        const conn = useConnectionStore.getState().connections.find(c => c.id === input.connectionId);
        connectionInfo = {
          connectionId: input.connectionId,
          connectionName: conn?.alias ? safeConnectionAlias(conn) : undefined,
          serverHost: safeConnectionServer(conn),
          database: input.database,
        };
      }

      // Create a new query tab input
      const newTabInput: NewQueryTabInput = {
        type: 'query',
        title: input.title || 'Query',
        connectionInfo,
        content: input.initialSql ? { query: input.initialSql } : undefined,
      };

      // addTab returns the new tab ID
      const tabId = addTab(newTabInput);
      setActiveTab(tabId);

      return { success: true, tabId };
    }

    if (input.type === 'table-explorer') {
      // Validate required fields for table-explorer
      if (!input.tableName) {
        return { success: false, error: 'tableName is required for table-explorer tabs' };
      }
      if (!input.connectionId) {
        return { success: false, error: 'connectionId is required for table-explorer tabs' };
      }
      if (!input.database) {
        return { success: false, error: 'database is required for table-explorer tabs' };
      }

      // Build connection info, resolving name/host from the connections store
      const teConn = useConnectionStore.getState().connections.find(c => c.id === input.connectionId);
      const connectionInfo: TabConnectionInfo = {
        connectionId: input.connectionId,
        connectionName: teConn?.alias,
        serverHost: safeConnectionServer(teConn),
        database: input.database,
      };

      // Create default content with the table info
      const defaultContent = createDefaultTableExplorerContent(
        input.tableName,
        input.schema || 'dbo',
        input.database
      );

      // Create a new table explorer tab input
      const newTabInput: NewTableExplorerTabInput = {
        type: 'table-explorer',
        title: input.title || `${input.tableName}`,
        connectionInfo,
        content: defaultContent,
      };

      // addTab returns the new tab ID
      const tabId = addTab(newTabInput);
      setActiveTab(tabId);

      return { success: true, tabId };
    }

    if (input.type === 'notebook') {
      let connectionInfo: TabConnectionInfo | undefined;
      if (input.connectionId) {
        const nbConn = useConnectionStore.getState().connections.find(c => c.id === input.connectionId);
        connectionInfo = {
          connectionId: input.connectionId,
          connectionName: nbConn?.alias,
          serverHost: safeConnectionServer(nbConn),
          database: input.database,
        };
      }

      // Build initial cells if provided
      const contentOverrides: Partial<NotebookTabContent> = {};
      if (input.initialCells?.length) {
        const cells: NotebookCell[] = input.initialCells.map((c) => {
          const id = crypto.randomUUID();
          return c.cellType === 'code'
            ? createCodeCell(id, c.source)
            : createMarkdownCell(id, c.source, true);
        });
        contentOverrides.cells = cells;
      }

      const newTabInput: NewNotebookTabInput = {
        type: 'notebook',
        title: input.title || 'Notebook',
        connectionInfo,
        ...(Object.keys(contentOverrides).length > 0 ? { content: contentOverrides } : {}),
      };

      const tabId = addTab(newTabInput);
      setActiveTab(tabId);

      return { success: true, tabId };
    }

    return { success: false, error: `Unknown tab type: ${input.type}` };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle focus_tab command
 */
const handleFocusTab = (tabId: string): UiFocusTabResult => {
  try {
    const { tabs, setActiveTab } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${tabId}` };
    }

    setActiveTab(tabId);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle set_editor_text command
 */
const handleSetEditorText = (input: UiSetEditorTextInput): UiSetEditorTextResult => {
  try {
    const { tabs, updateTabContent } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (isNotebookTab(tab)) {
      // At `block` this write is refused: it is the same mutation
      // `notebook_set_cell_source` performs, and that tool refuses there.
      // See `resolveEditorTextWriteScope` in `electron/mcp-ui-control`.
      if (input.notebookCellsAllowed === false) {
        return {
          success: false,
          error:
            'UI control is disabled for notebook cells at this permission level. Enable it in Settings → AI Integrations or in the connection settings.',
        };
      }
      // For notebook tabs, set the first code cell's source
      const content = tab.content as NotebookTabContent;
      const firstCodeCell = content.cells.find(isCodeCell);
      if (!firstCodeCell) {
        return { success: false, error: 'No code cell found in notebook' };
      }
      const updatedCells = content.cells.map((cell) =>
        cell.id === firstCodeCell.id ? { ...cell, source: input.sql } : cell
      );
      updateTabContent(input.tabId, { cells: updatedCells, isDirty: true });
      return { success: true };
    }

    if (tab.type !== 'query') {
      return { success: false, error: 'Can only set text on query or notebook tabs' };
    }

    updateTabContent(input.tabId, { query: input.sql });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle close_tab command
 */
const handleCloseTab = (input: UiCloseTabInput): UiCloseTabResult => {
  try {
    const { tabs, removeTab } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    // An open or starting transaction is the user's to end: an agent cannot
    // answer the close dialog, so it is refused (the user closes the tab).
    if (isQueryTab(tab) && (
      tab.content.transactionState === 'active'
      || tab.content.transactionState === 'error'
      || tab.content.transactionBeginToken !== undefined
    )) {
      return { success: false, error: 'This tab has an open or starting transaction; commit or roll it back first.' };
    }
    // A file edited in a storage tab's preview and not saved yet is the user's to keep or drop.
    if (!input.force && hasUnsavedStoragePreview(input.tabId)) {
      return { success: false, error: 'This tab has unsaved edits in its file preview; the user must save or discard them first.' };
    }
    // TODO: Add dirty check if !input.force
    removeTab(input.tabId);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle lock_tab command
 */
const handleLockTab = (input: UiLockTabInput): UiLockTabResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    const { isLocked, setLock } = useAiLockStore.getState();

    if (isLocked(input.tabId)) {
      return { success: false, error: 'Tab is already locked' };
    }

    const lockInfo: AiLockInfo = {
      sessionId: MCP_SESSION_ID,
      tabId: input.tabId,
      tabName: tab.title,
      lockedAt: new Date().toISOString(),
      message: input.message,
      isMetaLock: false,
    };

    setLock(input.tabId, lockInfo);

    // Mirror lock to AI Workspace .meta files for file-based agents
    blueprintLockToAiWorkspaces(input.tabId, input.message);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle unlock_tab command
 */
const handleUnlockTab = (input: UiUnlockTabInput): UiUnlockTabResult => {
  try {
    const { isLocked, getLockInfo, removeLock } = useAiLockStore.getState();

    if (!isLocked(input.tabId)) {
      // Already unlocked - still mirror to ensure .meta files are cleaned up
      blueprintUnlockToAiWorkspaces(input.tabId);
      return { success: true };
    }

    const lockInfo = getLockInfo(input.tabId);

    // Only allow unlocking if the lock was created by MCP session
    if (lockInfo && lockInfo.sessionId !== MCP_SESSION_ID) {
      return {
        success: false,
        error: 'Cannot unlock tab locked by a different session',
      };
    }

    removeLock(input.tabId);

    // Mirror unlock to AI Workspace .meta files for file-based agents
    blueprintUnlockToAiWorkspaces(input.tabId);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle check_abort command
 */
const handleCheckAbort = (input: UiCheckAbortInput): UiCheckAbortResult => {
  try {
    const { getLockInfo } = useAiLockStore.getState();
    const lockInfo = getLockInfo(input.tabId);

    return {
      success: true,
      abortRequested: lockInfo?.abortRequested ?? false,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle connect_to_database command
 */
const handleConnectToDatabase = async (
  input: UiConnectToDatabaseInput
): Promise<UiConnectToDatabaseResult> => {
  try {
    const store = useConnectionStore.getState();
    const connection = store.connections.find((c) => c.id === input.connectionId);

    if (!connection) {
      return { success: false, error: `Connection not found: ${input.connectionId}` };
    }

    const success = await store.connectToDatabase(connection);
    if (success) {
      return { success: true };
    }

    const errorMessage = useConnectionStore.getState().connectionErrorById[input.connectionId];
    return { success: false, error: redactSecretText(errorMessage || 'Failed to connect to database') };
  } catch (error) {
    return {
      success: false,
      // Goes to an agent (MCP connections_connect): never a secret-bearing target the driver echoed.
      error: redactSecretText(error instanceof Error ? error.message : 'Unknown error'),
    };
  }
};

/**
 * Handle set_table_filter command
 */
const handleSetTableFilter = (input: UiSetTableFilterInput): UiSetTableFilterResult => {
  try {
    const { tabs, updateTabContent } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (isStorageExplorerTab(tab)) {
      const result = setEntityTabFilter(tab, input.filters);
      if (!result.success) return result;
      updateTabContent<'storage-explorer'>(input.tabId, { entityView: result.entityView });
      return { success: true };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only set filters on table-explorer tabs' };
    }

    // Convert MCP filter format to workspace filter format
    const filters: FilterGroup[] = input.filters.map((fg) => ({
      field: fg.field,
      conditions: fg.conditions.map((c) => ({
        operator: c.operator,
        value: c.value,
        value2: c.value2,
      })),
    }));

    // Update tab content with new filters and reset to page 1
    updateTabContent(input.tabId, {
      filters,
      currentPage: 1,
    } as Partial<TableExplorerTabContent>);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle set_table_sort command
 */
const handleSetTableSort = (input: UiSetTableSortInput): UiSetTableSortResult => {
  try {
    const { tabs, updateTabContent } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only set sorting on table-explorer tabs' };
    }

    // Convert MCP sort format to workspace sort format
    const sorting: SortDefinition[] = input.sorting.map((s) => ({
      field: s.field,
      direction: s.direction,
    }));

    // Update tab content with new sorting and reset to page 1
    updateTabContent(input.tabId, {
      sorting,
      currentPage: 1,
    } as Partial<TableExplorerTabContent>);

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle set_table_pagination command
 */
const handleSetTablePagination = (input: UiSetTablePaginationInput): UiSetTablePaginationResult => {
  try {
    const { tabs, updateTabContent } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only set pagination on table-explorer tabs' };
    }

    const content = tab.content as TableExplorerTabContent;
    const updates: Partial<TableExplorerTabContent> = {};

    // Update page size if provided
    if (input.pageSize !== undefined) {
      if (input.pageSize < 1 || input.pageSize > 10000) {
        return { success: false, error: 'pageSize must be between 1 and 10000' };
      }
      updates.limit = input.pageSize;
      // Reset to page 1 when changing page size
      updates.currentPage = 1;
    }

    // Update page number if provided
    if (input.page !== undefined) {
      if (input.page < 1) {
        return { success: false, error: 'page must be >= 1' };
      }
      // Validate page doesn't exceed total pages if we know total row count
      if (content.totalRowCount !== undefined) {
        const pageSize = updates.limit ?? content.limit;
        const totalPages = Math.ceil(content.totalRowCount / pageSize);
        if (input.page > totalPages && totalPages > 0) {
          return { success: false, error: `page ${input.page} exceeds total pages (${totalPages})` };
        }
      }
      updates.currentPage = input.page;
    }

    if (Object.keys(updates).length > 0) {
      updateTabContent(input.tabId, updates);
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle get_table_snapshot command
 */
const handleGetTableSnapshot = (input: UiGetTableSnapshotInput): UiGetTableSnapshotResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (isStorageExplorerTab(tab)) {
      const snapshot = entityTabAsTableExplorerView(tab, input.maxPreviewRows ?? 10);
      return snapshot
        ? { success: true, snapshot }
        : { success: false, error: 'Can only get snapshot of table-explorer tabs' };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only get snapshot of table-explorer tabs' };
    }

    const content = tab.content as TableExplorerTabContent;
    const maxRows = input.maxPreviewRows ?? 10;

    // Calculate total pages
    const totalPages = content.totalRowCount !== undefined
      ? Math.ceil(content.totalRowCount / content.limit)
      : undefined;

    // Helper to convert Date to ISO string or keep primitive value
    const serializeScalar = (val: unknown): string | number | boolean | null | undefined => {
      if (val instanceof Date) {
        return val.toISOString();
      }
      if (val === undefined || val === null || typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        return val;
      }
      return String(val);
    };

    // `IN` / `NOT IN` carry a `(string | number)[]` value — serialize each
    // element with its own type preserved instead of flattening the list
    // into one joined string (see UiTableFilterCondition.value doc).
    const serializeValue = (val: unknown): string | number | boolean | null | (string | number)[] | undefined => {
      if (Array.isArray(val)) {
        return val.map((entry) => (typeof entry === 'number' ? entry : String(entry)));
      }
      return serializeScalar(val);
    };

    // Build snapshot
    const snapshot: TableExplorerSnapshot = {
      tabId: tab.id,
      table: {
        name: content.tableName,
        schema: content.schema,
        database: content.database,
      },
      connectionId: tab.connectionInfo?.connectionId,
      columns: content.columns.map((c) => ({
        name: c.name,
        dataType: c.dataType,
        nullable: c.nullable,
        isPrimaryKey: c.isPrimaryKey,
        isForeignKey: c.isForeignKey,
      })),
      filters: content.filters.map((fg) => ({
        field: fg.field,
        conditions: fg.conditions.map((c) => ({
          operator: c.operator,
          value: serializeValue(c.value),
          value2: serializeValue(c.value2) as string | number | undefined,
        })),
      })),
      sorting: content.sorting.map((s) => ({
        field: s.field,
        direction: s.direction,
      })),
      pagination: {
        pageSize: content.limit,
        currentPage: content.currentPage ?? 1,
        totalRows: content.totalRowCount,
        totalPages,
      },
      previewRows: content.rows.slice(0, maxRows),
      isLoading: content.isLoading ?? false,
      error: content.error,
    };

    return { success: true, snapshot };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle execute_query command - triggers execution in the UI
 */
const handleExecuteQuery = async (input: UiExecuteQueryInput): Promise<UiExecuteQueryResult> => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (isNotebookTab(tab)) {
      // For notebook tabs, queue execution via the notebook MCP store. The
      // classified list travels with it: `execute_active_cell` runs whichever
      // cell is active, and the runner refuses unless that cell's live text is
      // one the gate approved. Carrying nothing here is what let a cell rewritten
      // while the approval dialog was open execute in place of the one the user
      // saw and approved.
      //
      // Awaited, not queued-and-forgotten. Returning `success: true` the moment
      // the command was queued meant a refused JavaScript cell, a markdown
      // active cell and a cell rewritten after classification all reported that
      // the query had run.
      const { useNotebookMcpStore } = await import('../../workspace/notebook/store/notebookMcpStore');
      return new Promise<UiExecuteQueryResult>((resolve) => {
        useNotebookMcpStore.getState().queueCommand(input.tabId, {
          type: 'execute_active_cell',
          allowedSources: input.allowedSources,
          resolve: (r) => {
            const outcome = r as { success: boolean; error?: string };
            resolve(
              outcome.success
                ? { success: true, executionId: `notebook-exec-${Date.now()}` }
                : { success: false, error: outcome.error ?? 'The cell was not executed' },
            );
          },
        });
      });
    }

    if (tab.type !== 'query') {
      return { success: false, error: 'Can only execute queries on query or notebook tabs' };
    }

    // Add request to the store - the promise resolves when QueryEditorTab picks
    // it up. A query tab has one buffer, so the classified list has one entry and
    // membership is equality; `expectedSql` is the terminal protocol's name for
    // the same binding, and the tab-side runner refuses if the buffer changed.
    // `allowedSources` is required upstream, so this cannot silently be omitted;
    // an empty list yields an expectation nothing can satisfy, which refuses.
    const result = await useAiExecutionStore.getState().addRequest({
      requestId: `exec-${Date.now()}`,
      tabId: input.tabId,
      selectedOnly: input.selectedOnly,
      requestedAt: Date.now(),
      expectedSql: input.allowedSources[0] ?? '',
    });

    if (result.success !== true && result.error === AI_EXECUTION_SQL_CHANGED) {
      // A refusal code, not prose — say what happened, in terms the agent can act on.
      return {
        success: false,
        error:
          'The tab contents changed after the permission check, so nothing ran. Request the execution again.',
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle get_execution_units — reports the SQL an execution of this tab would
 * actually send, so the main-process permission gate can classify it.
 *
 * Notebooks report one unit per code cell rather than a concatenation: the
 * classifier refuses multi-statement text, so a joined notebook would always be
 * denied. Markdown cells never execute.
 *
 * JavaScript cells contribute no units, and this comment used to explain that
 * as "they run in a sandbox with no database access". Both halves were wrong —
 * the `vm` context shares host intrinsics, so a JS cell reaches `child_process`
 * — and worse, *reporting nothing here used to mean permitting everything*: an
 * empty unit list is an `allow`, and the run then went ahead unclassified.
 *
 * The units are still empty, because there is genuinely no SQL to classify. The
 * refusal now lives where the cell actually runs (`useJavaScriptExecution.runJsCell`),
 * which is also the only place that can see the cell's type at execution time
 * rather than at classification time.
 */
const handleGetExecutionUnits = (input: UiGetExecutionUnitsInput): UiGetExecutionUnitsResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (isNotebookTab(tab)) {
      const cells = (tab.content as NotebookTabContent).cells;
      if (input.scope === 'cell') {
        const cell = cells.find((c) => c.id === input.cellId);
        if (!cell) return { success: false, tabType: 'notebook', error: `Cell not found: ${input.cellId}` };
        // A non-code cell runs no SQL: nothing to classify, nothing to refuse.
        return { success: true, tabType: 'notebook', units: isCodeCell(cell) ? [cell.source] : [] };
      }
      return { success: true, tabType: 'notebook', units: cells.filter(isCodeCell).map((c) => c.source) };
    }

    if (tab.type !== 'query') {
      return { success: false, tabType: 'other', error: 'Tab does not execute SQL' };
    }

    return { success: true, tabType: 'query', units: [(tab.content as QueryTabContent).query ?? ''] };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle list_tabs command
 */
const handleListTabs = (): UiListTabsResult => {
  try {
    const { tabs, activeTabId } = useWorkspaceStore.getState();

    const tabList = tabs.map((tab) => {
      const baseInfo = {
        id: tab.id,
        type: tab.type,
        title: tab.title,
        isActive: tab.id === activeTabId,
        connectionId: tab.connectionInfo?.connectionId,
        database: tab.connectionInfo?.database,
      };

      if (isTableExplorerTab(tab)) {
        return {
          ...baseInfo,
          tableName: tab.content.tableName,
          schema: tab.content.schema,
        };
      }

      return baseInfo;
    });

    return { success: true, tabs: tabList };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle refresh_table command
 */
const handleRefreshTable = (input: UiRefreshTableInput): UiRefreshTableResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only refresh table-explorer tabs' };
    }

    // Queue the refresh command for the component to pick up
    useTableExplorerMcpStore.getState().queueCommand(input.tabId, { type: 'refresh' });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Convert MCP chart config to internal ChartConfig format
 */
const convertMcpChartConfig = (mcpConfig: McpChartConfig): ChartConfig => {
  const yAxis: AggregatedColumnMapping[] = mcpConfig.yAxis.map((y) => ({
    column: y.column,
    label: y.label,
    aggregation: y.aggregation,
  }));

  return {
    type: mcpConfig.type,
    xAxis: {
      column: mcpConfig.xAxisColumn,
      label: mcpConfig.xAxisLabel,
    },
    yAxis,
    series: mcpConfig.seriesColumn ? { column: mcpConfig.seriesColumn } : undefined,
    title: mcpConfig.title,
    showLegend: mcpConfig.showLegend ?? true,
    showGrid: mcpConfig.showGrid ?? true,
    barVariant: mcpConfig.barVariant,
    areaVariant: mcpConfig.areaVariant,
  };
};

/** A result column as available for resolving an MCP chart config's column references. */
type McpChartColumnRef = ChartRefColumn;

/**
 * Resolve one column reference from an MCP chart config to its row key,
 * with an error message an agent can act on when it can't be resolved.
 *
 * `notebook_get_cell` documents `key` as the value `notebook_set_chart_config`
 * expects, so a direct key match always wins. For agents still following the
 * old name-based docs, a bare column NAME that matches exactly one column
 * resolves to that column's key; a name shared by two or more columns cannot
 * be resolved (the agent must pick a key) and is rejected. The resolution
 * rule itself lives in `resolveChartColumnRef` (shared with the chart
 * data-shaping seam) — this is a thin adapter that turns its outcome into
 * the MCP-shaped result/error.
 */
const resolveMcpChartColumnRef = (
  ref: string,
  columns: McpChartColumnRef[]
): { key: string } | { error: string } => {
  const resolvedKey = resolveChartColumnRef(ref, columns);
  if (columns.some((c) => c.key === resolvedKey)) return { key: resolvedKey };

  const nameMatches = columns.filter((c) => c.name === ref);
  const availableKeys = columns.map((c) => c.key).join(', ');
  if (nameMatches.length > 1) {
    return {
      error: `Column "${ref}" is ambiguous — ${nameMatches.length} columns share that name. Use one of its keys instead: ${nameMatches.map((c) => c.key).join(', ')}`,
    };
  }
  return { error: `Column "${ref}" not found. Available column keys: ${availableKeys}` };
};

/**
 * Resolve every column reference (`xAxisColumn`, `yAxis[].column`,
 * `seriesColumn`) in an MCP chart config to row keys, against the columns
 * of the result set the chart will read. When no column info is available
 * (e.g. the cell has not run yet), the config passes through unresolved —
 * there is nothing to validate against.
 */
const resolveMcpChartConfigColumns = (
  config: McpChartConfig,
  columns: McpChartColumnRef[]
): { config: McpChartConfig } | { error: string } => {
  if (columns.length === 0) return { config };

  const xResolved = resolveMcpChartColumnRef(config.xAxisColumn, columns);
  if ('error' in xResolved) return { error: `xAxisColumn: ${xResolved.error}` };

  const yAxis: McpChartConfig['yAxis'] = [];
  for (const y of config.yAxis) {
    const resolved = resolveMcpChartColumnRef(y.column, columns);
    if ('error' in resolved) return { error: `yAxis.column: ${resolved.error}` };
    yAxis.push({ ...y, column: resolved.key });
  }

  let seriesColumn = config.seriesColumn;
  if (seriesColumn) {
    const resolved = resolveMcpChartColumnRef(seriesColumn, columns);
    if ('error' in resolved) return { error: `seriesColumn: ${resolved.error}` };
    seriesColumn = resolved.key;
  }

  return { config: { ...config, xAxisColumn: xResolved.key, yAxis, seriesColumn } };
};

/**
 * Handle show_chart command
 */
const handleShowChart = (input: UiShowChartInput): UiShowChartResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only show chart on table-explorer tabs' };
    }

    // Queue the show_chart command for the component to pick up
    useTableExplorerMcpStore.getState().queueCommand(input.tabId, {
      type: 'show_chart',
      show: input.show,
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle set_chart_config command
 */
const handleSetChartConfig = (input: UiSetChartConfigInput): UiSetChartConfigResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only set chart config on table-explorer tabs' };
    }

    // Convert MCP config to internal format
    const chartConfig = convertMcpChartConfig(input.config);

    // Queue the set_chart_config command for the component to pick up
    useTableExplorerMcpStore.getState().queueCommand(input.tabId, {
      type: 'set_chart_config',
      config: chartConfig,
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle select_row command
 */
const handleSelectRow = (input: UiSelectRowInput): UiSelectRowResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only select rows in table-explorer tabs' };
    }

    const content = tab.content as TableExplorerTabContent;
    if (input.rowIndex < 0 || input.rowIndex >= content.rows.length) {
      return { success: false, error: `Row index ${input.rowIndex} out of bounds (0-${content.rows.length - 1})` };
    }

    // Queue the select_row command for the component to pick up
    useTableExplorerMcpStore.getState().queueCommand(input.tabId, {
      type: 'select_row',
      rowIndex: input.rowIndex,
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle show_row_details command
 */
const handleShowRowDetails = (input: UiShowRowDetailsInput): UiShowRowDetailsResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isTableExplorerTab(tab)) {
      return { success: false, error: 'Can only show row details on table-explorer tabs' };
    }

    const content = tab.content as TableExplorerTabContent;

    // Validate row index if provided
    if (input.rowIndex !== undefined) {
      if (input.rowIndex < 0 || input.rowIndex >= content.rows.length) {
        return { success: false, error: `Row index ${input.rowIndex} out of bounds (0-${content.rows.length - 1})` };
      }
    }

    // Queue the show_row_details command for the component to pick up
    useTableExplorerMcpStore.getState().queueCommand(input.tabId, {
      type: 'show_row_details',
      show: input.show,
      layout: input.layout || 'side',
      rowIndex: input.rowIndex,
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

// ============================================================================
// Query Editor MCP Command Handlers
// ============================================================================

/**
 * Handle query_select_row command
 */
const handleQuerySelectRow = (input: UiQuerySelectRowInput): UiQuerySelectRowResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      return { success: false, error: 'Can only select rows in query tabs' };
    }

    const content = tab.content as QueryTabContent;
    const resultSetIndex = input.resultSetIndex ?? 0;
    const resultSet = content.resultSets?.[resultSetIndex];

    if (!resultSet) {
      return { success: false, error: `Result set ${resultSetIndex} not found` };
    }

    if (input.rowIndex < 0 || input.rowIndex >= (resultSet.rows?.length ?? 0)) {
      return { success: false, error: `Row index ${input.rowIndex} out of bounds` };
    }

    // Queue the command for the component to pick up
    useQueryEditorMcpStore.getState().queueCommand(input.tabId, {
      type: 'select_row',
      rowIndex: input.rowIndex,
      resultSetIndex,
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_show_row_details command
 */
const handleQueryShowRowDetails = (input: UiQueryShowRowDetailsInput): UiQueryShowRowDetailsResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      return { success: false, error: 'Can only show row details in query tabs' };
    }

    // Queue the command for the component to pick up
    useQueryEditorMcpStore.getState().queueCommand(input.tabId, {
      type: 'show_row_details',
      show: input.show,
      rowIndex: input.rowIndex,
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_switch_tab command
 */
const handleQuerySwitchTab = (input: UiQuerySwitchTabInput): UiQuerySwitchTabResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      return { success: false, error: 'Can only switch tabs in query tabs' };
    }

    // Queue the command for the component to pick up
    useQueryEditorMcpStore.getState().queueCommand(input.tabId, {
      type: 'switch_tab',
      resultTab: input.resultTab,
      resultSetIndex: input.resultSetIndex,
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_set_result_display command
 */
const handleQuerySetResultDisplay = (input: UiQuerySetResultDisplayInput): UiQuerySetResultDisplayResult => {
  try {
    const { tabs, updateTabContent } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      return { success: false, error: 'Can only set result display mode in query tabs' };
    }

    const content = tab.content as QueryTabContent;
    updateTabContent(input.tabId, {
      editorOptions: {
        wordWrap: content.editorOptions?.wordWrap ?? 'off',
        formatOnExecute: content.editorOptions?.formatOnExecute ?? false,
        resultDisplayMode: input.displayMode,
      },
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_execute_with_plan command
 */
const handleQueryExecuteWithPlan = (input: UiQueryExecuteWithPlanInput): UiQueryExecuteWithPlanResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      return { success: false, error: 'Can only execute with plan in query tabs' };
    }

    // Queue the command for the component to pick up. The 'actual' arm carries
    // what the permission gate classified — the tab refuses if the buffer changed
    // in between — and the type has no way to queue an 'actual' run without it.
    useQueryEditorMcpStore.getState().queueCommand(
      input.tabId,
      input.planMode === 'actual'
        ? { type: 'execute_with_plan', planMode: 'actual', allowedSources: input.allowedSources }
        : { type: 'execute_with_plan', planMode: 'estimated' }
    );

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_get_execution_plan command
 */
const handleQueryGetExecutionPlan = (input: UiQueryGetExecutionPlanInput): UiQueryGetExecutionPlanResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      return { success: false, error: 'Can only get execution plan from query tabs' };
    }

    const content = tab.content as QueryTabContent;
    const plan = content.executionPlan;

    if (!plan) {
      return { success: true, planId: undefined, summary: undefined };
    }

    return {
      success: true,
      planId: plan.planId,
      summary: {
        planId: plan.planId,
        engine: plan.summary.engine,
        mode: plan.summary.mode,
        format: plan.summary.format,
        capturedAt: plan.summary.capturedAt,
        statementCount: plan.summary.statementCount,
        warningsCount: plan.summary.warningsCount,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_get_messages command
 */
const handleQueryGetMessages = (input: UiQueryGetMessagesInput): UiQueryGetMessagesResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      return { success: false, error: 'Can only get messages from query tabs' };
    }

    const content = tab.content as QueryTabContent;
    const messages = content.messages ?? [];

    return {
      success: true,
      messages: messages.map((m) => ({
        id: m.id,
        type: m.type,
        content: m.content,
        timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : String(m.timestamp),
      })),
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_set_chart_config command
 */
const handleQuerySetChartConfig = (input: UiQuerySetChartConfigInput): UiQuerySetChartConfigResult => {
  try {
    console.log('[MCP UI Control] handleQuerySetChartConfig called', input);
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      console.log('[MCP UI Control] Tab not found:', input.tabId);
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      console.log('[MCP UI Control] Not a query tab:', tab.type);
      return { success: false, error: 'Can only set chart config in query tabs' };
    }

    // Convert MCP config to internal format
    const chartConfig = convertMcpChartConfig(input.config);
    console.log('[MCP UI Control] Converted chart config:', chartConfig);

    // Queue the command for the component to pick up
    const commandId = useQueryEditorMcpStore.getState().queueCommand(input.tabId, {
      type: 'set_chart_config',
      config: chartConfig,
      resultSetIndex: input.resultSetIndex,
    });
    console.log('[MCP UI Control] Queued set_chart_config command:', commandId);

    return { success: true };
  } catch (error) {
    console.error('[MCP UI Control] handleQuerySetChartConfig error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_show_chart command
 */
const handleQueryShowChart = (input: UiQueryShowChartInput): UiQueryShowChartResult => {
  try {
    console.log('[MCP UI Control] handleQueryShowChart called', input);
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      console.log('[MCP UI Control] Tab not found:', input.tabId);
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      console.log('[MCP UI Control] Not a query tab:', tab.type);
      return { success: false, error: 'Can only show chart in query tabs' };
    }

    // Queue the command for the component to pick up
    const commandId = useQueryEditorMcpStore.getState().queueCommand(input.tabId, {
      type: 'show_chart',
      show: input.show,
    });
    console.log('[MCP UI Control] Queued show_chart command:', commandId);

    return { success: true };
  } catch (error) {
    console.error('[MCP UI Control] handleQueryShowChart error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle query_get_results command
 */
const handleQueryGetResults = (input: UiQueryGetResultsInput): UiQueryGetResultsResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);

    if (!tab) {
      return { success: false, error: `Tab not found: ${input.tabId}` };
    }

    if (!isQueryTab(tab)) {
      return { success: false, error: 'Can only get results from query tabs' };
    }

    const content = tab.content as QueryTabContent;
    const resultSets = content.resultSets ?? [];
    const resultSetIndex = input.resultSetIndex ?? 0;
    const maxRows = input.maxRows ?? 50;

    if (resultSetIndex >= resultSets.length) {
      return {
        success: true,
        resultSetCount: resultSets.length,
        columns: [],
        rows: [],
        totalRowCount: 0,
        truncated: false,
      };
    }

    const resultSet = resultSets[resultSetIndex];
    const allRows = Array.isArray(resultSet.rows) ? resultSet.rows : [];
    const truncated = allRows.length > maxRows;
    const rows = truncated ? allRows.slice(0, maxRows) : allRows;

    return {
      success: true,
      resultSetCount: resultSets.length,
      columns: resultSet.columns?.map((c) => ({
        name: c.name,
        dataType: c.dataType,
        nullable: c.nullable,
        isPrimaryKey: c.isPrimaryKey,
        isForeignKey: c.isForeignKey,
      })),
      rows,
      totalRowCount: allRows.length,
      truncated,
      stats: content.stats ? {
        executionTimeMs: content.stats.executionTimeMs,
        totalRowCount: content.stats.totalRowCount,
        returnedRowCount: content.stats.returnedRowCount,
        affectedRowCount: content.stats.affectedRowCount,
      } : undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Handle export_results — the ONE agent path into either results grid's export.
 *
 * It runs the grid's own handler (registered in `gridExportRegistry`), so the
 * file an agent asks for is byte-for-byte the file the user's own menu
 * produces. Everything here is a precondition check: the work itself belongs
 * to the grid.
 */
const handleUiExportResults = async (
  input: UiExportResultsInput,
): Promise<UiExportResultsResult> => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);
    if (!tab) return { success: false, error: `Tab not found: ${input.tabId}` };

    const isQuery = isQueryTab(tab);
    if (!isQuery && !isTableExplorerTab(tab)) {
      return { success: false, error: 'Only query editor and table explorer tabs have results to export' };
    }

    const runner = getGridExportRunner(input.tabId);
    if (!runner) {
      // Open tabs stay mounted, so a registered grid is the norm — this is the
      // tab whose grid has not rendered at all yet (a table explorer still
      // resolving its table, say).
      return {
        success: false,
        error: 'That tab has no results grid yet — run the query (or load the table) first.',
      };
    }

    // Argument validity first, state second: an agent that passed an
    // impossible combination should hear about that, not about the grid.
    if (input.allResultSets && !isQuery) {
      return { success: false, error: 'allResultSets applies to query editor tabs only' };
    }
    if (input.allResultSets && input.format !== 'xlsx' && input.format !== 'json') {
      return {
        success: false,
        error: 'allResultSets is Excel and JSON only — the other formats hold a single result set.',
      };
    }

    const scope = input.scope ?? 'loaded';
    if (scope === 'all-rows') {
      if (input.format !== 'csv') {
        return {
          success: false,
          error: 'scope "all-rows" is CSV only — the streaming export writes CSV. Use scope "loaded" for the other formats.',
        };
      }
      if (!runner.exportAllCsv) {
        return {
          success: false,
          error: isQuery
            ? 'This result set holds every row already, so there is nothing to stream — use scope "loaded".'
            : 'Table Explorer pages through a table and has no all-rows export. Run the query in a query tab and export that, or use scope "loaded" for the rows on screen.',
        };
      }
      return await runner.exportAllCsv();
    }

    if (runner.loadedRowCount() === 0) {
      // A query tab keeps its grid mounted with zero rows before the first run,
      // so this — not the missing-runner branch above — is what an agent
      // exporting too early actually hits. Say what to do next or it retries.
      return {
        success: false,
        error: 'No rows are loaded in that grid yet — run the query (or load the table) first.',
      };
    }

    return await runner.exportLoaded({
      format: input.format,
      scope: input.allResultSets ? 'all' : 'active',
    });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Handle notebook_run_cell - queues via MCP store, resolved by component hook
 */
const handleNotebookRunCell = async (input: UiNotebookRunCellInput): Promise<UiNotebookRunCellResult> => {
  try {
    const result = handleNotebookGetCell({ tabId: input.tabId, cellId: input.cellId });
    if (!result.success) return { success: false, error: result.error };
    if (result.cellType !== 'code') return { success: false, error: 'Can only run code cells' };

    const { useNotebookMcpStore } = await import('../../workspace/notebook/store/notebookMcpStore');
    return new Promise<UiNotebookRunCellResult>((resolve) => {
      useNotebookMcpStore.getState().queueCommand(input.tabId, {
        type: 'run_cell',
        cellId: input.cellId,
        // Carries the classified sources down to the cell runner, which reads the
        // live cell and refuses anything not in the list.
        allowedSources: input.allowedSources,
        resolve: (r) => resolve(r as UiNotebookRunCellResult),
      });
    });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Handle notebook_run_all - queues via MCP store, resolved by component hook
 */
const handleNotebookRunAll = async (input: UiNotebookRunAllInput): Promise<UiNotebookRunAllResult> => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);
    if (!tab) return { success: false, error: `Tab not found: ${input.tabId}` };
    if (!isNotebookTab(tab)) return { success: false, error: 'Not a notebook tab' };

    const { useNotebookMcpStore } = await import('../../workspace/notebook/store/notebookMcpStore');
    return new Promise<UiNotebookRunAllResult>((resolve) => {
      useNotebookMcpStore.getState().queueCommand(input.tabId, {
        type: 'run_all',
        continueOnError: input.continueOnError ?? false,
        // Every classified code-cell source; the runner re-checks each cell
        // against this list immediately before that cell executes.
        allowedSources: input.allowedSources,
        resolve: (r) => resolve(r as UiNotebookRunAllResult),
      });
    });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Handle notebook_stop_execution - queues stop command
 */
const handleNotebookStopExecution = async (tabId: string): Promise<UiNotebookStopExecutionResult> => {
  try {
    const { useNotebookMcpStore } = await import('../../workspace/notebook/store/notebookMcpStore');
    useNotebookMcpStore.getState().queueCommand(tabId, { type: 'stop_execution' });
    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Handle notebook_restart_session - queues via MCP store
 */
const handleNotebookRestartSession = async (input: UiNotebookRestartSessionInput): Promise<UiNotebookRestartSessionResult> => {
  try {
    const { useNotebookMcpStore } = await import('../../workspace/notebook/store/notebookMcpStore');
    return new Promise<UiNotebookRestartSessionResult>((resolve) => {
      useNotebookMcpStore.getState().queueCommand(input.tabId, {
        type: 'restart_session',
        resolve: (r) => resolve(r as UiNotebookRestartSessionResult),
      });
    });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Handle notebook_save - queues via MCP store
 */
const handleNotebookSave = async (input: UiNotebookSaveInput): Promise<UiNotebookSaveResult> => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);
    if (!tab) return { success: false, error: `Tab not found: ${input.tabId}` };
    if (!isNotebookTab(tab)) return { success: false, error: 'Not a notebook tab' };

    const content = tab.content as NotebookTabContent;
    if (!content.filePath) {
      // The agent cannot supply one: choosing where bytes land on the user's
      // disk is not a database capability. Saving to a path the user already
      // picked is.
      return {
        success: false,
        error: 'This notebook has no file yet. Save it once yourself (File → Save) to choose where it lives; after that this tool can save it again.',
      };
    }

    const { useNotebookMcpStore } = await import('../../workspace/notebook/store/notebookMcpStore');
    return new Promise<UiNotebookSaveResult>((resolve) => {
      useNotebookMcpStore.getState().queueCommand(input.tabId, {
        type: 'save',
        saveMode: input.saveMode,
        resolve: (r) => resolve(r as UiNotebookSaveResult),
      });
    });
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Handle notebook_set_chart_config - directly updates the cell's chartConfig in the store
 */
const handleNotebookSetChartConfig = (input: UiNotebookSetChartConfigInput): UiNotebookSetChartConfigResult => {
  try {
    const { tabs } = useWorkspaceStore.getState();
    const tab = tabs.find((t) => t.id === input.tabId);
    if (!tab) return { success: false, error: `Tab not found: ${input.tabId}` };
    if (!isNotebookTab(tab)) return { success: false, error: 'Not a notebook tab' };

    const content = tab.content as NotebookTabContent;
    const cell = content.cells.find((c) => c.id === input.cellId);
    if (!cell) return { success: false, error: `Cell not found: ${input.cellId}` };
    if (cell.cellType !== 'code' && cell.cellType !== 'javascript') {
      return { success: false, error: 'Chart config can only be set on code or JavaScript cells' };
    }

    let chartConfig: ChartConfig | undefined;
    if (input.config) {
      const execCell = isExecutableCell(cell) ? cell : undefined;
      const availableColumns: McpChartColumnRef[] =
        execCell?.outputs?.resultSets[0]?.columns?.map((c) => ({
          name: c.name ?? '',
          key: c.id ?? c.name ?? '',
        })) ?? [];
      const resolved = resolveMcpChartConfigColumns(input.config, availableColumns);
      if ('error' in resolved) return { success: false, error: resolved.error };
      chartConfig = convertMcpChartConfig(resolved.config);
    }
    const updatedCells = content.cells.map((c) =>
      c.id === input.cellId ? { ...c, chartConfig } : c
    );
    useWorkspaceStore.getState().updateTabContent(input.tabId, { cells: updatedCells, isDirty: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Handle get_plsql_editor_content command.
 */
const handleGetPlsqlEditorContent = (input: UiGetPlsqlEditorContentInput): UiGetPlsqlEditorContentResult => {
  const tab = useWorkspaceStore.getState().tabs.find((t) => t.id === input.tabId);
  if (!tab) return { success: false, error: `Tab not found: ${input.tabId}` };
  if (!isPlSqlEditorTab(tab)) return { success: false, error: `Tab is not a PL/SQL editor: ${tab.type}` };

  const c = tab.content;
  const currentSource = c.sourceView === 'spec' ? c.specSource : (c.bodySource ?? c.source);

  return {
    success: true,
    objectName: c.objectName,
    schema: c.schema,
    database: c.database,
    objectType: c.objectType,
    sourceView: c.sourceView,
    source: currentSource,
    specSource: c.specSource,
    bodySource: c.bodySource,
    sourceModified: c.sourceModified,
    compiledWithDebug: c.compiledWithDebug,
    compilationErrors: c.compilationErrors,
    members: c.members?.map((m) => ({
      name: m.name,
      type: m.type,
      overloadIndex: m.overloadIndex,
      params: m.params?.map((p) => ({ name: p.name, dataType: p.dataType, direction: p.mode })),
      returnType: m.returnType,
    })),
    mode: c.mode,
    debugState: c.debugState,
    sessionKey: c.sessionKey,
    connectionId: tab.connectionInfo?.connectionId,
  };
};

// ---------------------------------------------------------------------------
// Package Explorer handlers
// ---------------------------------------------------------------------------

/**
 * Open/reuse a Package Explorer tab and select the given package.
 */
const handlePackageSelect = (input: UiPackageSelectInput): UiPackageSelectResult => {
  try {
    const { addTab, setActiveTab, tabs } = useWorkspaceStore.getState();
    const conn = useConnectionStore.getState().connections.find(c => c.id === input.connectionId);

    const existing = findExistingPackageExplorerTab(tabs, input.connectionId, input.database);
    let tabId: string;
    if (existing) {
      tabId = existing.id;
      setActiveTab(tabId);
    } else {
      tabId = addTab({
        title: `Package Explorer — ${input.database}`,
        type: 'package-explorer',
        connectionInfo: {
          connectionId: input.connectionId,
          connectionName: conn?.alias ? safeConnectionAlias(conn) : undefined,
          serverHost: safeConnectionServer(conn),
          database: input.database,
        },
        content: createDefaultPackageExplorerContent({
          connectionId: input.connectionId,
          database: input.database,
        }),
      });
      setActiveTab(tabId);
    }

    // Select the package via the pins store
    usePackagePinsStore.getState().setLastViewed(input.connectionId, {
      schema: input.schema,
      name: input.packageName,
    });

    return { success: true, tabId };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Select a package and navigate to a specific member in its body.
 */
const handlePackageNavigateMember = (input: UiPackageNavigateMemberInput): UiPackageNavigateMemberResult => {
  try {
    // First, select the package (opens/reuses Package Explorer tab)
    const selectResult = handlePackageSelect({
      connectionId: input.connectionId,
      database: input.database,
      schema: input.schema,
      packageName: input.packageName,
    });
    if (!selectResult.success) return selectResult;

    // The virtual plsql-editor tab is created by PackageExplorerDetail.
    // Set targetMember on it so the editor scrolls to the member once the source loads.
    const virtualTabId = `${selectResult.tabId}::pkg::${input.schema}.${input.packageName}`;
    const { tabs, updateTabContent } = useWorkspaceStore.getState();
    const virtualTab = tabs.find(t => t.id === virtualTabId);
    if (virtualTab && isPlSqlEditorTab(virtualTab)) {
      updateTabContent(virtualTabId, { targetMember: { name: input.memberName, type: 'procedure' } });
    }
    // If virtual tab doesn't exist yet, it will be created by PackageExplorerDetail
    // and the member navigation will happen on next render cycle

    return { success: true, tabId: selectResult.tabId };
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Process a UI control command and return the result.
 *
 * Exported so the renderer half of the MCP permission model can be driven
 * directly in tests: several commands make their own refusal here (a notebook
 * cell write withheld at `block`, an execution whose bound text no longer
 * matches), and those refusals have to be provable of this router rather than
 * only of the main-process gate that sets the flags.
 */
export const processCommand = async (payload: UiControlCommandPayload): Promise<UiControlResult> => {
  const { command } = payload;
  console.log(`[MCP UI Control] processCommand: ${command.type}`, payload);

  switch (command.type) {
    case 'open_tab':
      return handleOpenTab(command.input);
    case 'focus_tab':
      return handleFocusTab(command.input.tabId);
    case 'set_editor_text':
      return handleSetEditorText(command.input);
    case 'close_tab':
      return handleCloseTab(command.input);
    case 'lock_tab':
      return handleLockTab(command.input);
    case 'unlock_tab':
      return handleUnlockTab(command.input);
    case 'check_abort':
      return handleCheckAbort(command.input);
    case 'connect_to_database':
      return await handleConnectToDatabase(command.input);
    case 'set_editor_selection':
      // Not implemented in MVP
      return { success: false, error: 'set_editor_selection not implemented' };
    case 'execute_query':
      return await handleExecuteQuery(command.input);
    case 'get_execution_units':
      return handleGetExecutionUnits(command.input);
    case 'set_table_filter':
      return handleSetTableFilter(command.input);
    case 'set_table_sort':
      return handleSetTableSort(command.input);
    case 'set_table_pagination':
      return handleSetTablePagination(command.input);
    case 'get_table_snapshot':
      return handleGetTableSnapshot(command.input);
    case 'list_tabs':
      return handleListTabs();
    case 'refresh_table':
      return handleRefreshTable(command.input);
    case 'show_chart':
      return handleShowChart(command.input);
    case 'set_chart_config':
      return handleSetChartConfig(command.input);
    case 'select_row':
      return handleSelectRow(command.input);
    case 'show_row_details':
      return handleShowRowDetails(command.input);
    // Query editor commands
    case 'query_select_row':
      return handleQuerySelectRow(command.input);
    case 'query_show_row_details':
      return handleQueryShowRowDetails(command.input);
    case 'query_switch_tab':
      return handleQuerySwitchTab(command.input);
    case 'query_set_result_display':
      return handleQuerySetResultDisplay(command.input);
    case 'query_execute_with_plan':
      return handleQueryExecuteWithPlan(command.input);
    case 'query_get_execution_plan':
      return handleQueryGetExecutionPlan(command.input);
    case 'query_get_messages':
      return handleQueryGetMessages(command.input);
    case 'query_set_chart_config':
      return handleQuerySetChartConfig(command.input);
    case 'query_show_chart':
      return handleQueryShowChart(command.input);
    case 'query_get_results':
      return handleQueryGetResults(command.input);
    case 'export_results':
      return await handleUiExportResults(command.input);
    // Notebook commands
    case 'notebook_list_cells':
      return handleNotebookListCells(command.input);
    case 'notebook_get_cell':
      return handleNotebookGetCell(command.input);
    case 'notebook_add_cell':
      return handleNotebookAddCell(command.input);
    case 'notebook_set_cell_source':
      return handleNotebookSetCellSource(command.input);
    case 'notebook_delete_cell':
      return handleNotebookDeleteCell(command.input);
    case 'notebook_move_cell':
      return handleNotebookMoveCell(command.input);
    case 'notebook_convert_cell':
      return handleNotebookConvertCell(command.input);
    case 'notebook_run_cell':
      return await handleNotebookRunCell(command.input);
    case 'notebook_run_all':
      return await handleNotebookRunAll(command.input);
    case 'notebook_stop_execution':
      return await handleNotebookStopExecution(command.input.tabId);
    case 'notebook_clear_outputs':
      return handleNotebookClearOutputs(command.input);
    case 'notebook_restart_session':
      return await handleNotebookRestartSession(command.input);
    case 'notebook_save':
      return await handleNotebookSave(command.input);
    case 'notebook_set_chart_config':
      return handleNotebookSetChartConfig(command.input);
    // PL/SQL editor commands
    case 'get_plsql_editor_content':
      return handleGetPlsqlEditorContent(command.input);
    case 'package_select':
      return handlePackageSelect(command.input);
    case 'package_navigate_member':
      return handlePackageNavigateMember(command.input);
    default:
      return { success: false, error: `Unknown command type: ${(command as { type: string }).type}` };
  }
};

/**
 * Hook to handle UI control commands from MCP server.
 * Should be used in a top-level component (e.g., App or MainShell).
 */
export function useMcpUiControl() {
  const setPendingRequest = useWriteApprovalStore((state) => state.setPendingRequest);
  const setPairingRequest = usePairingApprovalStore((state) => state.setPendingRequest);
  const setConnectionsAddRequest = useConnectionsAddApprovalStore((state) => state.setPendingRequest);
  const setMetaInfoDeclareRequest = useMetaInfoDeclareApprovalStore((state) => state.setPendingRequest);

  const handleCommand = useCallback((payload: UiControlCommandPayload) => {
    console.log('[MCP UI Control] handleCommand received:', payload.command.type, payload);
    void (async () => {
      try {
        const result = await processCommand(payload);
        console.log('[MCP UI Control] Command result:', payload.command.type, result);
        window.electronAPI?.mcpUiControl?.sendResult(payload.commandId, result);
      } catch (error) {
        window.electronAPI?.mcpUiControl?.sendResult(payload.commandId, {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        } as UiControlResult);
      }
    })();
  }, []);

  const handleWriteApprovalRequest = useCallback(
    (request: McpWriteApprovalRequest) => {
      console.log('[MCP UI Control] Received write approval request:', request.requestId);
      setPendingRequest(request);
    },
    [setPendingRequest]
  );

  const handlePairingApprovalRequest = useCallback(
    (request: McpPairingApprovalRequest) => {
      console.log('[MCP UI Control] Received pairing approval request:', request.requestId);
      setPairingRequest(request);
    },
    [setPairingRequest]
  );

  const handleConnectionsAddApprovalRequest = useCallback(
    (request: McpConnectionsAddApprovalRequest) => {
      console.log('[MCP UI Control] Received connections_add approval request:', request.requestId);
      setConnectionsAddRequest(request);
    },
    [setConnectionsAddRequest]
  );

  const handleMetaInfoDeclareApprovalRequest = useCallback(
    (request: McpMetaInfoDeclareApprovalRequest) => {
      console.log('[MCP UI Control] Received metainfo_declare approval request:', request.requestId);
      setMetaInfoDeclareRequest(request);
    },
    [setMetaInfoDeclareRequest]
  );

  // Handle MCP-initiated transaction state updates
  useEffect(() => {
    const cleanup = window.electronAPI?.transaction?.onMcpStateSync((update) => {
      const { updateTabContent, tabs } = useWorkspaceStore.getState();
      const tab = tabs.find((t) => t.id === update.tabId);
      if (!tab || !isQueryTab(tab)) return;

      const patch: Partial<QueryTabContent> = {};
      if (update.transactionState !== undefined) patch.transactionState = update.transactionState;
      // An MCP commit/rollback ends the session: clear its identity with the
      // state, as every other "transaction over" path does (`TRANSACTION_TAB_IDLE_RESET`).
      if (update.transactionState === 'idle') patch.transactionSessionGeneration = undefined;
      if (update.transactionMode !== undefined) patch.transactionMode = update.transactionMode;
      if (update.isolationLevel !== undefined) patch.isolationLevel = update.isolationLevel;
      updateTabContent(update.tabId, patch);
    });
    return () => { cleanup?.(); };
  }, []);

  useEffect(() => {
    // Subscribe to UI control commands from main process
    const commandCleanup = window.electronAPI?.mcpUiControl?.onExecuteCommand(handleCommand);

    // Subscribe to write approval requests from main process
    const approvalCleanup = window.electronAPI?.mcpUiControl?.onWriteApprovalRequest(
      handleWriteApprovalRequest
    );

    // Subscribe to CLI pairing approval requests from main process
    const pairingCleanup = window.electronAPI?.mcpUiControl?.onPairingApprovalRequest(
      handlePairingApprovalRequest
    );

    // Subscribe to connections_add approval requests from main process
    const connectionsAddCleanup = window.electronAPI?.mcpUiControl?.onConnectionsAddApprovalRequest(
      handleConnectionsAddApprovalRequest
    );

    // Subscribe to metainfo_declare approval requests from main process
    const metaInfoDeclareCleanup = window.electronAPI?.mcpUiControl?.onMetaInfoDeclareApprovalRequest(
      handleMetaInfoDeclareApprovalRequest
    );

    return () => {
      commandCleanup?.();
      approvalCleanup?.();
      pairingCleanup?.();
      connectionsAddCleanup?.();
      metaInfoDeclareCleanup?.();
    };
  }, [handleCommand, handleWriteApprovalRequest, handlePairingApprovalRequest, handleConnectionsAddApprovalRequest, handleMetaInfoDeclareApprovalRequest]);
}
